import { useFirestoreUsersApproved, useFirestore } from "../../firebase/useFirestore";
import { client } from '../../hooks/Client';
import { db } from "../../firebase/config";
import { useState, useContext } from "react";
import deleteIcon from '../../images/icons/delete-icon.png'
import dummyPhoto from '../../images/Design/dummy-photo.jpeg'
import firebase from "../../firebase/compat";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import usePermissions from "../../hooks/usePermissions";
import useSettings from "../../hooks/Settings";
import mergeTeamRows from "../../helpers/mergeTeamRows";
import normalizeEmail from "../../helpers/normalizeEmail";
import callTeamFunction, { teamStatusMessage } from "../../helpers/team/callTeamFunction";
import Modal from "../common/Modal";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Table from "../common/table/Table.jsx";
import TableCell from "../common/table/TableCell.jsx";

/**
 * @param {object} props
 * @param {string} props.selectedOption - Het tabblad dat op de instellingenpagina open staat
 * @param {(option: string) => void} props.setSelectedOption - Wisselt van tabblad; gebruikt om naar Rollen te springen
 */
const Team = ({selectedOption, setSelectedOption}) => {
    // Context
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // State
    // Raw as typed; `invitedEmail` below is the value everything else uses.
    const [email, setEmail] = useState('')
    const [inviteModalOpen, setInviteModalOpen] = useState(false)
    const [addToPersonas, setAddToPersonas] = useState(false)
    const [inviteRole, setInviteRole] = useState('')
    // Loopt het uitnodigen nog (opslaan + mail)? Dan is de knop uit, zodat een
    // tweede klik niet alles opnieuw verstuurt.
    const [inviting, setInviting] = useState(false)

    const goToRolesTab = () => {
        if (setSelectedOption) setSelectedOption('7')
    }

    // The single address the whole invite flow works with. Everything downstream
    // — the server's "is this person already registered?" check, the stored invitation,
    // the mail — has to use the exact same string, or an address pasted in with
    // a stray space silently splits into two different people.
    // See docs/inbox.md Bug #137.
    const invitedEmail = normalizeEmail(email)

    // Hooks
    const { has, userRoles } = usePermissions()
    const { personasOn } = useSettings()
    // Firestore
    const members = useFirestoreUsersApproved()
    const invites = useFirestore("Invitations")

    // Registered members + pending invitations as one list for the table
    const teamRows = mergeTeamRows(members, invites)

    const emailHandler = (e) => {
        const value = e.target.value

        setEmail(value)
    }


    const inviteMemberHandler = async () => {

        // Zonder adres is er niets te versturen: een lege waarde zou een
        // uitnodiging met Email: '' wegschrijven en naar niemand mailen.
        if (!invitedEmail.includes('@')) {
            alert('Vul een geldig e-mailadres in.')
            return
        }

        // Of iemand al een account heeft, beslist de server op Firebase Auth.
        // Dat stond hier als een Users-query op e-mailadres, maar er bestaan
        // profielen waarvan het inlogaccount verwijderd is: die kregen de route
        // voor bestaande gebruikers, en een mail om in te loggen met een
        // account dat niet bestaat.
        setInviting(true)
        try {
            const status = await addExistingMember()

            if (status === 'success') {
                await sendInviteMail('existing')
            } else if (status === 'no-auth-account') {
                // Not registered yet: store a pending invite carrying the chosen
                // role, and only then mail — a mail without an invitation leaves
                // the registration stranded.
                const stored = await createInvitation()
                if (stored) await sendInviteMail('new')
            } else if (status === 'no-profile') {
                alert(
                    `${invitedEmail} heeft al een inlogaccount, maar er hoort geen profiel bij. `
                    + `Neem contact op met Deccos via info@deccos.nl.`
                )
            } else if (status !== null) {
                alert(teamStatusMessage(status))
            }
        } finally {
            setInviting(false)
        }

        setAddToPersonas(false)
        setInviteRole('')
    }

    // Pending invite for a not-yet-registered email. The server reuses the row
    // if one already exists for this email, so re-inviting just updates the
    // role — and checks that the inviter may hand out that role.
    // Een mislukte opslag meldt callTeamFunction zelf; er gaat dan geen mail uit.
    const createInvitation = () => callTeamFunction('saveInvitation', {
        client: client,
        email: invitedEmail,
        roleId: inviteRole,
        addToPersonas: addToPersonas,
    })

    /**
     * Voegt de genodigde toe als hij al een account heeft. De server zoekt
     * het account op in Firebase Auth en leest het profiel zelf.
     *
     * @returns {Promise<'success' | 'no-auth-account' | 'no-profile' | 'invalid-email' | 'error' | null>}
     *   De status van de server, of `null` als de aanroep zelf mislukte (al gemeld).
     */
    const addExistingMember = async () => {

        const updateUserAccountWhenInvited = firebase.app().functions('europe-west1').httpsCallable('updateUserAccountWhenInvited')

        try {
            const result = await updateUserAccountWhenInvited({
                email: invitedEmail,
                client: client,
                addToPersonas: addToPersonas,
                userRoleId: inviteRole,
            })
            return result.data
        } catch (err) {
            console.error('updateUserAccountWhenInvited error:', err)
            alert(
                `Uitnodigen is mislukt: ${err.message || 'onbekende fout'}. `
                + `Neem contact op met Deccos via info@deccos.nl.`
            )
            return null
        }

    }

    /**
     * De uitnodigingsmail. Wordt server-side samengesteld en verstuurd.
     *
     * Stond hier tot 2026-09-08 als twee losse schrijfacties naar de
     * Email-collectie. Dat had drie problemen die met de verhuizing verdwijnen:
     * iedere ingelogde gebruiker kon namens elke organisatie mailen (de
     * Firestore-regel op Email staat create toe voor iedereen), de link werd
     * 'https://undefined/...' op elk webadres zonder ImpactManagers-document
     * (inbox #102), en de mail was een kale reeks br-tags terwijl er inmiddels
     * een sjabloon is.
     *
     * Het webadres gaat mee als hint: zolang CompagnyMeta.Brand nog niet overal
     * ingevuld is, houdt dat het merk gelijk aan hoe het vandaag werkt. De
     * server legt die hostname tegen zijn eigen merkenlijst en vertrouwt hem niet.
     *
     * AI-CONTEXT: See docs/features/updates-mailing.md
     *
     * @param {'existing' | 'new'} kind - Heeft de genodigde al een account?
     */
    const sendInviteMail = async (kind) => {

        const sendTeamInvite = firebase.app().functions('europe-west1').httpsCallable('sendTeamInvite')

        try {
            await sendTeamInvite({
                client: client,
                email: invitedEmail,
                kind: kind,
                hostname: window.location.hostname,
            })
            setSaved(true)
            setInviteModalOpen(false)
        } catch (err) {
            // Laten zien dat het misging: voorheen viel een mislukte schrijfactie
            // stil, en dacht de uitnodiger dat de mail onderweg was.
            console.error('sendTeamInvite error:', err)
            alert(
                `De uitnodiging kon niet verstuurd worden: ${err.message || 'onbekende fout'}. `
                + `Neem contact op met Deccos via info@deccos.nl.`
            )
        }

    }

    const deleteUser = (e) => {

        const docid = e.currentTarget.dataset.id

        db.collection('Users')
        .doc(docid)
        .update({
            Compagny: firebase.firestore.FieldValue.arrayRemove(client),
        })
        .then(() => {
            setSaved(true)
        })
    }

    // Member role handler — the server writes the per-tenant role into the
    // UserRoleIDs map, after checking the caller may hand out that role.
    const memberRoleHandler = async (e) => {

        const docid = e.target.dataset.id
        const value = e.target.options[e.target.selectedIndex].value

        const ok = await callTeamFunction('setMemberRole', { client: client, userDocId: docid, roleId: value })
        if (ok) setSaved(true)
    }

    // Role handler for a pending invite row — the server writes the role onto the invite
    const inviteRoleHandler = async (e) => {

        const docid = e.target.dataset.id
        const value = e.target.options[e.target.selectedIndex].value

        const ok = await callTeamFunction('setInvitationRole', { client: client, invitationDocId: docid, roleId: value })
        if (ok) setSaved(true)
    }

    // Remove a pending invite
    // Soft delete (ADR-008): flag the invite instead of removing it.
    const deleteInvite = (e) => {

        const docid = e.target.dataset.id

        db.collection('Invitations')
        .doc(docid)
        .update({
            Deleted: true,
            DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
            DeletedBy: auth.ID,
        })
        .then(() => {
            setSaved(true)
        })
    }

    return (
        <>
            {selectedOption === '2' &&
                <div className="settings-container">
                    <div className="profile-inner-container">
                        <div className='table-container'>
                            <h2>Team</h2>
                            <Table
                                columns={[
                                    { key: 'photo', label: 'FOTO', searchable: false },
                                    { key: 'name', label: 'NAAM' },
                                    { key: 'email', label: 'E-MAIL', searchable: false },
                                    { key: 'role', label: 'ROL', searchable: false },
                                    { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                                ]}
                                items={teamRows}
                                getItemKey={(row) => `${row._type}-${row.ID}`}
                                renderRow={(row) => row._type === 'invite' ? (
                                    <>
                                        <TableCell>
                                            <img src={dummyPhoto} alt="" id='members-photo'/>
                                        </TableCell>
                                        <TableCell>
                                            <span className="invite-pending-badge">Uitgenodigd</span>
                                        </TableCell>
                                        <TableCell>
                                            <p>{row.Email}</p>
                                        </TableCell>
                                        <TableCell>
                                            <select name="" id="" data-id={row.docid} value={row.UserRoleID || ''} onChange={inviteRoleHandler}>
                                                <option value="">-- Selecteer een rol --</option>
                                                {userRoles && userRoles.map(role => (
                                                    <option key={role.docid} value={role.ID}>{role.Name}</option>
                                                ))}
                                            </select>
                                            <EditOutlinedIcon className='table-delete-icon' onClick={goToRolesTab} />

                                        </TableCell>
                                        <TableCell>
                                            {has('team.delete') && (
                                                <img src={deleteIcon} alt="" className="userrole-users-delete-button" data-id={row.docid} onClick={deleteInvite} />
                                            )}
                                        </TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell>
                                            <img src={row.Photo} alt="" id='members-photo'/>
                                        </TableCell>
                                        <TableCell>
                                            <p>{row.UserName}</p>
                                        </TableCell>
                                        <TableCell>
                                            <p>{row.Email}</p>
                                        </TableCell>
                                        <TableCell>
                                            <select name="" id="" data-id={row.docid} value={row.UserRoleIDs?.[client] || ''} onChange={memberRoleHandler}>
                                                <option value="">-- Selecteer een rol --</option>
                                                {userRoles && userRoles.map(role => (
                                                    <option key={role.docid} value={role.ID}>{role.Name}</option>
                                                ))}
                                            </select>
                                            <EditOutlinedIcon className='table-delete-icon' onClick={goToRolesTab} />

                                        </TableCell>
                                        <TableCell>
                                            {has('team.delete') && (
                                                <img src={deleteIcon} alt="" className="userrole-users-delete-button" data-id={row.docid} onClick={deleteUser} />
                                            )}
                                        </TableCell>
                                    </>
                                )}
                                searchAccessor={(row) => `${row?.UserName || ''} ${row?.Email || ''}`}
                                searchPlaceholder="Zoeken in team"
                                emptyMessage="Nog geen teamleden gevonden."
                                addRow={has('team.invite') ? {
                                    toolTipContent: 'Teamlid uitnodigen',
                                    onClick: () => setInviteModalOpen(true),
                                } : undefined}
                            />
                        </div>
                    </div>
                    <Modal
                    modalOpen={inviteModalOpen}
                    setModalOpen={setInviteModalOpen}
                    pageHeader={'Teamlid uitnodigen'}
                    confirm={inviteMemberHandler}
                    confirmDisabled={inviting}
                    confirmLabel={inviting ? 'Bezig met uitnodigen…' : 'Uitnodigen'}
                    content={
                        <div className="invite-form">
                            <div className="invite-form-field">
                                <label className="invite-form-label" htmlFor="invite-email">E-mailadres</label>
                                <input id="invite-email" type="text" placeholder='Noteer hier het emailadres' onChange={emailHandler} />
                            </div>
                            <div className="invite-form-field">
                                <label className="invite-form-label" htmlFor="invite-role">Rol</label>
                                <select id="invite-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                                    <option value="">-- Selecteer een rol --</option>
                                    {userRoles && userRoles.map(role => (
                                        <option key={role.docid} value={role.ID}>{role.Name}</option>
                                    ))}
                                </select>
                                <p className="invite-form-hint">Bepaalt wat dit teamlid kan zien en bewerken.</p>
                            </div>
                            {personasOn === 'flex' && (
                                <div className="invite-form-field">
                                    <label className="invite-form-label">Ondersteuning</label>
                                    <label className='add-options-checkbox-label'>
                                        <input type="checkbox" checked={addToPersonas} onChange={(e) => setAddToPersonas(e.target.checked)} />
                                        Toevoegen aan medewerkers in Ondersteuning
                                    </label>
                                </div>
                            )}
                        </div>
                    }
                    />
                </div>
            }
        </>
    )
}

export default Team
